import { useState, useEffect } from "react";
import { ethers } from "ethers";
import {
  Box,
  Text,
  VStack,
  HStack,
  Spinner,
  Badge,
  Divider,
  List,
  ListItem,
  Icon,
  IconButton,
  useColorModeValue,
  Tooltip,
  Button,
} from "@chakra-ui/react";
import { CheckCircleIcon, WarningIcon, ArrowBackIcon } from "@chakra-ui/icons";
import { websockets, publicWebsockets } from "../config";
import { useNavigate } from "react-router-dom";
const getProviderName = (url) => {
  const providerMap = {
    "wss://arbitrum-one-rpc.publicnode.com": "PublicNode",
    "wss://arbitrum.drpc.org": "dRPC",
    "wss://arbitrum.gateway.tenderly.co": "Tenderly",
    "wss://arbitrum.callstaticrpc.com": "CallStaticRPC",
    "wss://arb1.arbitrum.io/feed": "Arbitrum Feed",
  };
  return providerMap[url] || "Unknown Public Provider";
};

const maskUrl = (url) => {
  try {
    const urlObj = new URL(url);
    return websockets.some(ws => ws.url === url) ? `${urlObj.protocol}//${urlObj.host}/******` : url;
  } catch (error) {
    return "Invalid URL";
  }
};

const WebSocketPerformanceTest = ({ setFastestWebSocket, fastestWebSocket }) => {
  const [results, setResults] = useState([]);
  const [publicResults, setPublicResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fastest, setFastest] = useState(null);
  const navigate = useNavigate();

  const testWebSockets = async (socketList, setResultCallback) => {
    await new Promise((resolve) => setTimeout(resolve, 4000)); 

    const performanceResults = await Promise.all(
      socketList.map(async ({ url, name }) => {
        if (!url) return { url: "❌ Undefined URL", latency: Infinity, providerName: "Unknown Provider" };

        console.log(`Testing WebSocket: ${url}`);
        const start = performance.now();
        let latency = null;
        let provider = null;

        try {
          provider = new ethers.providers.WebSocketProvider(url);

          await Promise.race([
            new Promise((resolve, reject) => {
              provider._websocket.onopen = resolve;
              provider._websocket.onerror = reject;
              setTimeout(() => reject(new Error("WebSocket Timeout")), 5000);
            }),
          ]);

          latency = performance.now() - start;
          console.log(`✅ ${url} - Latency: ${latency.toFixed(2)} ms`);
        } catch (error) {
          console.error(`❌ Error testing ${url}:`, error.message);
          latency = Infinity;
        } finally {
          if (provider && typeof provider.destroy === "function") {
            provider.destroy();
          }
        }
        

        return { url, latency, providerName: name || getProviderName(url) };
      })
    );

    performanceResults.sort((a, b) => a.latency - b.latency);
    setResultCallback(performanceResults);
  };

  const runWebSocketTests = async () => {
    setLoading(true);
    console.log("Running WebSocket tests...");

    await testWebSockets(websockets, setResults);
    await testWebSockets(publicWebsockets.map(url => ({ url, name: getProviderName(url) })), setPublicResults);
    setLoading(false);
  };

  useEffect(() => {
    runWebSocketTests();
  }, []);

  useEffect(() => {
    if (results.length > 0) {
      const bestWebSocket = results.find((result) => result.latency !== Infinity);
      if (bestWebSocket) {
        setFastest(bestWebSocket.url);
        setFastestWebSocket(bestWebSocket.url);
      }
    }
  }, [results, setFastestWebSocket]);

  const bgColor = useColorModeValue("white", "gray.800");

  return (
    <Box
      p={4}
      maxW={{ base: "100%", md: "container.md" }}
      mx="auto"
      borderWidth="1px"
      borderRadius="lg"
      boxShadow="md"
      bg={bgColor}
      borderColor={"gray.400"}
    >
      <HStack justify="space-between" mb={3} flexWrap="wrap">
        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold">
          WebSocket Latency Test
        </Text>
        <HStack justify="space-between" mb={3} flexWrap="wrap">
        <IconButton
        size={{ base: "sm", md: "md" }}
          icon={<ArrowBackIcon />}
          bg='dodgerblue'
          color='white'
          onClick={() => navigate("/")}
          aria-label="Go to Homepage"
        />
      <Tooltip label="Reload WebSocket Tests" aria-label="Reload tooltip">
        <Button
          bg='dodgerblue'
          color='white'
          size={{ base: "sm", md: "md" }}
          isLoading={loading}
          onClick={runWebSocketTests}
          isDisabled={loading}
        >
          Start Test
        </Button>
        </Tooltip>
      </HStack>
       
      </HStack>
      
      <Text fontSize="lg" fontWeight="bold" mb={2}>Private WebSockets</Text>
      <List spacing={2}>
        {results.map((result, index) => (
          <ListItem key={index}>
            <HStack bg={result.latency === Infinity ? "red.100" : "green.100"} p={2} borderRadius="md" justify="space-between">
              <HStack>
                <Icon as={result.latency === Infinity ? WarningIcon : CheckCircleIcon} color={result.latency === Infinity ? "red.500" : "green.500"} />
                <Text fontSize={{ base: "xs", md: "sm" }}>{result.providerName} - {maskUrl(result.url)}</Text>
              </HStack>
              <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="bold">
                {result.latency === Infinity ? "❌ Failed" : `✅ ${result.latency.toFixed(2)} ms`}
              </Text>
            </HStack>
          </ListItem>
        ))}
      </List>
      <Divider my={3} />
      <Text fontSize="lg" fontWeight="bold" mb={2}>Public WebSockets</Text>
      <List spacing={2}>
        {publicResults.map((result, index) => (
          <ListItem key={index}>
            <HStack bg={result.latency === Infinity ? "red.100" : "green.100"} p={2} borderRadius="md" justify="space-between">
              <HStack>
                <Icon as={result.latency === Infinity ? WarningIcon : CheckCircleIcon} color={result.latency === Infinity ? "red.500" : "green.500"} />
                <Text fontSize={{ base: "xs", md: "sm" }}>{result.providerName} - {result.url}</Text>
              </HStack>
              <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="bold">
                {result.latency === Infinity ? "❌ Failed" : `✅ ${result.latency.toFixed(2)} ms`}
              </Text>
            </HStack>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default WebSocketPerformanceTest;
